import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, setDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { storage, db } from '../firebase/config';

// Resize and convert an image to an efficient format (WebP preferred) before upload
export const processMenuImage = async (
  file: File,
  maxWidth = 1000,
  quality = 0.82
): Promise<File> => {
  try {
    // Skip processing for tiny images
    if (file.size < 80 * 1024) {
      return file;
    }

    const objectUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = (e) => reject(e);
        image.src = objectUrl;
      });

      const scale = Math.min(1, maxWidth / img.width);
      const targetWidth = Math.max(1, Math.round(img.width * scale));
      const targetHeight = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Try WebP first, fall back to JPEG if not supported
      const blob: Blob | null = await new Promise((resolve) => {
        canvas.toBlob(
          (b: Blob | null) => {
            if (b) return resolve(b);
            // Fallback to JPEG
            canvas.toBlob((b2) => resolve(b2), 'image/jpeg', quality);
          },
          'image/webp',
          quality
        );
      });

      if (!blob) return file;

      const originalName = file.name;
      const baseName = originalName.replace(/\.[^.]+$/, '');
      const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
      const processedName = `${baseName}.${ext}`;
      const processedFile = new File([blob], processedName, { type: blob.type });
      return processedFile;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (e) {
    console.warn('Image processing failed, uploading original file:', e);
    return file;
  }
};

export const uploadImage = async (file: File, path: string): Promise<string> => {
  try {
    console.log('Starting image upload...', { fileName: file.name, fileSize: file.size, path });
    
    // Create a reference to the file location
    const imageRef = ref(storage, `menu-items/${path}`);
    console.log('Created storage reference:', imageRef.fullPath);
    
    // Upload the file
    console.log('Uploading bytes...');
    const snapshot = await uploadBytes(imageRef, file);
    console.log('Upload completed:', snapshot);
    
    // Get the download URL
    console.log('Getting download URL...');
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Download URL obtained:', downloadURL);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    console.error('Error details:', {
      code: (error as any)?.code,
      message: (error as any)?.message,
      stack: (error as any)?.stack
    });
    throw new Error(`Failed to upload image: ${(error as any)?.message || 'Unknown error'}`);
  }
};

export const deleteImage = async (imageUrl: string): Promise<void> => {
  try {
    console.log('🗑️ Deleting image from Storage and Firestore:', imageUrl);
    
    // Step 1: Delete from Firebase Storage
    const url = new URL(imageUrl);
    const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
    const imageRef = ref(storage, path);
    await deleteObject(imageRef);
    console.log('✅ Image deleted from Storage');
    
    // Step 2: Delete image record from Firestore
    const imagesQuery = query(
      collection(db, 'images'),
      where('url', '==', imageUrl)
    );
    const imagesSnapshot = await getDocs(imagesQuery);
    
    const deletePromises = imagesSnapshot.docs.map(imageDoc => 
      deleteDoc(doc(db, 'images', imageDoc.id))
    );
    
    await Promise.all(deletePromises);
    console.log(`✅ Deleted ${deletePromises.length} image record(s) from Firestore`);
    
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw error if file doesn't exist (already deleted)
    if ((error as any)?.code !== 'storage/object-not-found') {
      throw new Error('Failed to delete image');
    }
  }
};

export const generateImagePath = (itemId: string, fileName: string): string => {
  const timestamp = Date.now();
  const extension = fileName.split('.').pop();
  return `${itemId}_${timestamp}.${extension}`;
};

export const saveImageRecord = async (imageData: {
  id: string;
  name: string;
  url: string;
  uploadedBy: string;
  type: string;
  size: number;
  itemId?: string;
}): Promise<void> => {
  try {
    const imageRecord = {
      ...imageData,
      uploadedAt: new Date(),
      isActive: true
    };
    
    await setDoc(doc(db, 'images', imageData.id), imageRecord);
    console.log('Image record saved:', imageData.id);
  } catch (error) {
    console.error('Error saving image record:', error);
    throw new Error('Failed to save image record');
  }
};

// Delete all image records associated with a menu item
export const deleteImageRecordsByItemId = async (itemId: string): Promise<void> => {
  try {
    console.log('🗑️ Deleting image records for item:', itemId);
    
    const imagesQuery = query(
      collection(db, 'images'),
      where('itemId', '==', itemId)
    );
    const imagesSnapshot = await getDocs(imagesQuery);
    
    const deletePromises = imagesSnapshot.docs.map(imageDoc => 
      deleteDoc(doc(db, 'images', imageDoc.id))
    );
    
    await Promise.all(deletePromises);
    console.log(`✅ Deleted ${deletePromises.length} image record(s) from Firestore for item ${itemId}`);
  } catch (error) {
    console.error('Error deleting image records:', error);
    // Don't throw - this is a cleanup operation
  }
};
